import * as React from 'react';
import * as CollapsiblePrimitive from '@radix-ui/react-collapsible';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@utils/cn';

/**
 * Section Root Component
 */
export interface SectionProps extends React.HTMLAttributes<HTMLElement> {
  /**
   * Section spacing
   */
  spacing?: 'sm' | 'md' | 'lg' | 'xl';
}

/**
 * Section Component
 *
 * A semantic section element with consistent spacing.
 *
 * @example
 * <Section spacing="lg">
 *   <h2>Patient Information</h2>
 *   <p>Patient details go here</p>
 * </Section>
 */
const Section = React.forwardRef<HTMLElement, SectionProps>(
  ({ className, spacing = 'md', ...props }, ref) => {
    const spacingClass = {
      sm: 'py-4',
      md: 'py-6',
      lg: 'py-8',
      xl: 'py-12',
    }[spacing];

    return (
      <section
        ref={ref}
        className={cn(spacingClass, className)}
        {...props}
      />
    );
  },
);
Section.displayName = 'Section';

/**
 * Collapsible Section Component
 */
export interface CollapsibleSectionProps {
  /**
   * Section title
   */
  title: React.ReactNode;
  /**
   * Section description (optional)
   */
  description?: React.ReactNode;
  /**
   * Initially open state
   */
  defaultOpen?: boolean;
  /**
   * Controlled open state
   */
  open?: boolean;
  /**
   * Callback when open state changes
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * Section content
   */
  children: React.ReactNode;
  /**
   * Wrapper className
   */
  className?: string;
  /**
   * Header className
   */
  headerClassName?: string;
  /**
   * Content className
   */
  contentClassName?: string;
  /**
   * Disable collapsing
   */
  disabled?: boolean;
  /**
   * Show divider at the bottom
   */
  divider?: boolean;
  /**
   * Icon position
   */
  iconPosition?: 'left' | 'right';
}

/**
 * CollapsibleSection Component
 *
 * A section that can be collapsed and expanded with smooth animations.
 * Built on Radix UI Collapsible for full accessibility.
 *
 * @example
 * <CollapsibleSection title="Medical History" defaultOpen>
 *   <p>Patient medical history details...</p>
 * </CollapsibleSection>
 *
 * @example
 * // Controlled collapsible section
 * const [open, setOpen] = useState(false);
 * <CollapsibleSection
 *   title="Lab Results"
 *   description="View patient laboratory test results"
 *   open={open}
 *   onOpenChange={setOpen}
 * >
 *   <LabResultsTable />
 * </CollapsibleSection>
 *
 * @example
 * // With divider
 * <CollapsibleSection
 *   title="Allergies"
 *   divider
 *   iconPosition="right"
 * >
 *   <AllergyList />
 * </CollapsibleSection>
 */
const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  description,
  defaultOpen = true,
  open,
  onOpenChange,
  children,
  className,
  headerClassName,
  contentClassName,
  disabled = false,
  divider = false,
  iconPosition = 'left',
}) => {
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen);
  const isControlled = open !== undefined;
  const isOpen = isControlled ? open : internalOpen;

  const handleOpenChange = (newOpen: boolean) => {
    if (!isControlled) {
      setInternalOpen(newOpen);
    }
    onOpenChange?.(newOpen);
  };

  return (
    <CollapsiblePrimitive.Root
      open={isOpen}
      onOpenChange={handleOpenChange}
      disabled={disabled}
      className={cn('space-y-4', divider && 'border-b pb-6', className)}
    >
      <CollapsiblePrimitive.Trigger
        className={cn(
          'flex w-full items-center justify-between gap-4 text-left transition-colors hover:text-accent-foreground',
          disabled && 'cursor-not-allowed opacity-50',
          headerClassName,
        )}
        disabled={disabled}
      >
        {iconPosition === 'left' && (
          <span className="shrink-0">
            {isOpen ? (
              <ChevronDown className="h-5 w-5" />
            ) : (
              <ChevronRight className="h-5 w-5" />
            )}
          </span>
        )}
        <div className="flex-1 space-y-1">
          <div className="font-semibold">{title}</div>
          {description && (
            <div className="text-sm text-muted-foreground">{description}</div>
          )}
        </div>
        {iconPosition === 'right' && (
          <span className="shrink-0">
            {isOpen ? (
              <ChevronDown className="h-5 w-5" />
            ) : (
              <ChevronRight className="h-5 w-5" />
            )}
          </span>
        )}
      </CollapsiblePrimitive.Trigger>

      <CollapsiblePrimitive.Content
        className={cn(
          'overflow-hidden transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down',
          contentClassName,
        )}
      >
        {children}
      </CollapsiblePrimitive.Content>
    </CollapsiblePrimitive.Root>
  );
};
CollapsibleSection.displayName = 'CollapsibleSection';

/**
 * Section Header Component
 */
export interface SectionHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Add divider at the bottom
   */
  divider?: boolean;
}

/**
 * SectionHeader Component
 *
 * A header component for sections, typically containing title and optional actions.
 *
 * @example
 * <SectionHeader divider>
 *   <div className="flex items-center justify-between">
 *     <h2>Patient Details</h2>
 *     <Button size="sm">Edit</Button>
 *   </div>
 * </SectionHeader>
 */
const SectionHeader = React.forwardRef<HTMLDivElement, SectionHeaderProps>(
  ({ className, divider, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('mb-4', divider && 'border-b pb-4', className)}
      {...props}
    />
  ),
);
SectionHeader.displayName = 'SectionHeader';

/**
 * Section Title Component
 */
export interface SectionTitleProps
  extends React.HTMLAttributes<HTMLHeadingElement> {
  /**
   * Heading level
   */
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
}

/**
 * SectionTitle Component
 *
 * A title component for sections.
 *
 * @example
 * <SectionTitle>Medical History</SectionTitle>
 * <SectionTitle as="h3">Vital Signs</SectionTitle>
 */
const SectionTitle = React.forwardRef<HTMLHeadingElement, SectionTitleProps>(
  ({ className, as: Component = 'h2', ...props }, ref) => (
    <Component
      ref={ref}
      className={cn(
        'text-2xl font-semibold leading-none tracking-tight',
        className,
      )}
      {...props}
    />
  ),
);
SectionTitle.displayName = 'SectionTitle';

/**
 * Section Description Component
 */
const SectionDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
SectionDescription.displayName = 'SectionDescription';

/**
 * Section Content Component
 */
const SectionContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('space-y-4', className)} {...props} />
));
SectionContent.displayName = 'SectionContent';

export {
  Section,
  CollapsibleSection,
  SectionHeader,
  SectionTitle,
  SectionDescription,
  SectionContent,
};
